/* eslint-disable react-hooks/rules-of-hooks */

import { useEffect, useRef } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';

import { usePressablePreloadRefNew } from './usePressablePreloadRefNew';

const PRELOAD_MAX_AGE = 5 * 60 * 1000; // 5 mins
const TIME_TO_INTENT = 200;

export type OnLoad<T> = (obj: { dispose: () => void; reference: T }) => void;

export type UsePressablePreloadRefProps<T> = {
	load: () => T;
	loadOnFocus?: boolean;
	onBeforeLoad?: () => void;
	onLoad: OnLoad<T>;
	preloadOnFocus?: boolean;
};

export function usePressablePreloadRef<T extends { dispose: () => void }>({
	load,
	onLoad,
	onBeforeLoad,
	loadOnFocus = false,
	preloadOnFocus = false,
}: UsePressablePreloadRefProps<T>) {
	if (fg('entry-points_new_usepressablepreloadref')) {
		return usePressablePreloadRefNew({ load, onLoad, onBeforeLoad, loadOnFocus, preloadOnFocus });
	}

	const ref = useRef<HTMLElement>(null);

	useEffect(() => {
		const el = ref.current;
		if (!el) {
			return;
		}

		const request: {
			load?: { dispose: () => void; reference: T };
			preload?: { dispose: () => void; reference: T };
			preloadIntentTimeout?: ReturnType<typeof setTimeout>;
			preloadMaxAgeTimeout?: ReturnType<typeof setTimeout>;
		} = {};

		const clearPreloadTimeouts = () => {
			if (typeof request.preloadIntentTimeout === 'number') {
				clearTimeout(request.preloadIntentTimeout);
				delete request.preloadIntentTimeout;
			}

			if (typeof request.preloadMaxAgeTimeout === 'number') {
				clearTimeout(request.preloadMaxAgeTimeout);
				delete request.preloadMaxAgeTimeout;
			}
		};

		const cancelPreload = () => {
			clearPreloadTimeouts();
			request.preload?.dispose();
		};

		const loadReference = () => {
			if (request.load) {
				return request.load;
			}

			const reference = request.preload?.reference ?? load();
			if (request.preload) {
				delete request.preload;
			}

			request.load = {
				reference,
				dispose() {
					reference.dispose();
					delete request.load;
				},
			};

			return request.load;
		};

		const clickListener = () => {
			clearPreloadTimeouts();
			if (onBeforeLoad) {
				onBeforeLoad();
			}
			onLoad(loadReference());
		};

		const mouseDownListener = () => {
			clearPreloadTimeouts();
			loadReference();
		};

		const preloadReference = () => {
			if (request.preloadIntentTimeout || request.preload) {
				return;
			}

			request.preloadIntentTimeout = setTimeout(() => {
				delete request.preloadIntentTimeout;

				const reference = load();

				request.preload = {
					reference,
					dispose() {
						reference.dispose();
						delete request.preload;
					},
				};

				request.preloadMaxAgeTimeout = setTimeout(() => {
					delete request.preloadMaxAgeTimeout;
					request.preload?.dispose();
				}, PRELOAD_MAX_AGE);
			}, TIME_TO_INTENT);
		};

		el.addEventListener('click', clickListener);
		el.addEventListener('mousedown', mouseDownListener);

		el.addEventListener('mouseenter', preloadReference);
		el.addEventListener('mouseleave', cancelPreload);

		if (loadOnFocus) {
			el.addEventListener('focusin', clickListener);
		} else if (preloadOnFocus) {
			el.addEventListener('focusin', preloadReference);
			el.addEventListener('focusout', cancelPreload);
		}

		return () => {
			cancelPreload();

			el.removeEventListener('click', clickListener);
			el.removeEventListener('mousedown', mouseDownListener);

			el.removeEventListener('mouseenter', preloadReference);
			el.removeEventListener('mouseleave', cancelPreload);

			if (loadOnFocus) {
				el.removeEventListener('focusin', clickListener);
			} else if (preloadOnFocus) {
				el.removeEventListener('focusin', preloadReference);
				el.removeEventListener('focusout', cancelPreload);
			}
		};
	}, [load, onBeforeLoad, onLoad, loadOnFocus, preloadOnFocus]);

	return ref;
}
